import { useEffect, useState, useRef } from 'react';
import { Building2, MapPin, Users, TrendingUp, Award, Target, Briefcase, Globe, Zap, Star } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Building2, MapPin, Users, TrendingUp, Award, Target, Briefcase, Globe, Zap, Star
};

export default function Milestones() {
  const [milestones, setMilestones] = useState([]);
  const [counts, setCounts] = useState({});
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/milestones`);
        setMilestones(response.data);
      } catch (error) {
        console.error('Error fetching milestones:', error);
      }
    };
    fetchMilestones();
  }, []);

  useEffect(() => {
    if (milestones.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          // Animate each milestone based on its value
          milestones.forEach((milestone, index) => {
            const numericValue = parseInt(milestone.value.replace(/[^0-9]/g, ''));
            if (!isNaN(numericValue)) {
              animateCount(index, numericValue, 2000);
            }
          });
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, milestones]);

  const animateCount = (key, target, duration) => {
    const increment = target / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCounts((prev) => ({ ...prev, [key]: target }));
        clearInterval(timer);
      } else {
        setCounts((prev) => ({ ...prev, [key]: Math.floor(current) }));
      }
    }, 16);
  };

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#F5F5F5]" data-testid="milestones-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
          {milestones.map((milestone, index) => {
            const Icon = iconMap[milestone.icon] || Building2;
            const hasPlus = milestone.value.includes('+');
            const animatedValue = counts[index] !== undefined ? counts[index] : 0;
            const displayValue = hasPlus ? `${animatedValue.toLocaleString()}+` : animatedValue.toLocaleString();
            
            return (
              <div
                key={milestone.id || index}
                className="text-center py-4 sm:py-0"
                data-testid={`milestone-${index}`}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-[#E12429] rounded-full mb-3 sm:mb-4">
                  <Icon className="text-white" size={28} />
                </div>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#222] mb-1 sm:mb-2">
                  {displayValue}
                </div>
                <div className="text-[#666] font-medium text-sm sm:text-base whitespace-nowrap">{milestone.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}